// Central exam-flow error mapper.
//
// Converts an Axios error (HTTP status + backend `code`) into a student-friendly
// Arabic error STATE that the UI (ExamErrorState.vue) renders. It NEVER echoes a
// raw backend/exception message — students only ever see the curated copy here.
//
// Returned shape:
//   { code, title, message, secondary?, icon, action: { label, to? , retry? }, startAt? }

const BACK_TO_EXAMS = { label: 'العودة إلى الامتحانات', to: '/exams' };
const RETRY = { label: 'إعادة المحاولة', retry: true };

const BY_CODE = {
  EXAM_NOT_FOUND: {
    title: 'الامتحان غير موجود',
    message: 'هذا الامتحان غير موجود أو لم يعد متاحًا.',
    secondary: 'برجاء الرجوع إلى المدرس للتأكد من بيانات الامتحان.',
    icon: 'search', action: BACK_TO_EXAMS,
  },
  EXAM_NOT_AVAILABLE: {
    title: 'الامتحان غير متاح',
    message: 'هذا الامتحان غير متاح لحسابك. برجاء الرجوع إلى المدرس.',
    icon: 'lock', action: BACK_TO_EXAMS,
  },
  EXAM_NOT_STARTED: {
    title: 'لم يبدأ الامتحان بعد',
    message: 'لم يبدأ وقت هذا الامتحان بعد.',
    icon: 'clock', action: BACK_TO_EXAMS,
  },
  EXAM_ENDED: {
    title: 'انتهى وقت الامتحان',
    message: 'انتهى وقت هذا الامتحان ولا يمكن بدء الامتحان الآن.',
    icon: 'clock', action: BACK_TO_EXAMS,
  },
  EXAM_ACCESS_DENIED: {
    title: 'غير مسموح',
    message: 'غير مسموح لك بدخول هذا الامتحان. برجاء الرجوع إلى المدرس.',
    icon: 'lock', action: BACK_TO_EXAMS,
  },
  EXAM_ATTEMPT_NOT_FOUND: {
    title: 'لا توجد محاولة',
    message: 'لم يتم العثور على محاولة الامتحان. برجاء الرجوع إلى قائمة الامتحانات والمحاولة مرة أخرى.',
    icon: 'alert', action: BACK_TO_EXAMS,
  },
  EXAM_ALREADY_SUBMITTED: {
    title: 'تم إنهاء الامتحان',
    message: 'لقد تم إنهاء هذا الامتحان بالفعل.',
    icon: 'check', action: BACK_TO_EXAMS,
  },
  // Existing content-protection code (SubscriptionGate) — kept friendly here too.
  SUBSCRIPTION_REQUIRED: {
    title: 'يتطلب اشتراك',
    message: 'للوصول إلى هذا الامتحان يجب الاشتراك في هذه السنة الدراسية.',
    icon: 'lock', action: { label: 'الاشتراك الآن', to: '/subscribe' },
  },
  NETWORK_ERROR: {
    title: 'تعذر الاتصال',
    message: 'تعذر الاتصال بالخادم. تأكد من اتصالك بالإنترنت وحاول مرة أخرى.',
    icon: 'wifi', action: RETRY,
  },
  SERVER_ERROR: {
    title: 'حدث خطأ غير متوقع',
    message: 'حدث خطأ أثناء تحميل الامتحان. برجاء المحاولة مرة أخرى.',
    secondary: 'إذا استمرت المشكلة، برجاء التواصل مع المدرس.',
    icon: 'alert', action: RETRY,
  },
};

export function mapExamError(error) {
  // No HTTP response at all → network/connectivity problem.
  if (!error || !error.response) {
    return { code: 'NETWORK_ERROR', ...BY_CODE.NETWORK_ERROR };
  }

  const status = error.response.status;
  const data = error.response.data || {};
  const code = String(data.code || '').toUpperCase();

  // 1) Prefer the backend's explicit, curated code.
  if (BY_CODE[code]) {
    const mapped = { code, ...BY_CODE[code] };
    if (code === 'EXAM_NOT_STARTED' && data.exam_start_at) mapped.startAt = data.exam_start_at;
    return mapped;
  }

  // 2) Fall back by HTTP status — NEVER surface data.message (may be a raw exception).
  if (status === 404) return { code: 'EXAM_NOT_FOUND', ...BY_CODE.EXAM_NOT_FOUND };
  if (status === 403) return { code: 'EXAM_ACCESS_DENIED', ...BY_CODE.EXAM_ACCESS_DENIED };
  if (status === 409) return { code: 'EXAM_ALREADY_SUBMITTED', ...BY_CODE.EXAM_ALREADY_SUBMITTED };
  if (status === 422) {
    return { code: 'VALIDATION', title: 'بيانات غير صحيحة', message: 'تعذّر إتمام العملية. برجاء المحاولة مرة أخرى.', icon: 'alert', action: BACK_TO_EXAMS };
  }
  // 500 / 502 / 503 / anything else.
  return { code: 'SERVER_ERROR', ...BY_CODE.SERVER_ERROR };
}

/** Display-only Egypt-time formatting (Africa/Cairo). Never used for countdown logic. */
export function formatEgyptTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ar-EG', {
      timeZone: 'Africa/Cairo', dateStyle: 'medium', timeStyle: 'short',
    });
  } catch (e) {
    return '';
  }
}
